import {
  BriefcaseIcon,
  BookOpenIcon,
  ThumbUpIcon,
  UsersIcon,
  ChevronDownIcon,
} from '@heroicons/react/outline';
import { Badge, Card } from '@yoast/ui-library';
import { useMemo } from 'react';

import type { QueryResultDetailed } from '../../api/generated/insights.schemas';
import { sentimentScoreToPercentage } from '../../utils/stringUtils';
import AISummarySection from './AISummarySection';
import BrandsSection from './BrandsSection';
import CitationsSection from './CitationsSection';
import SentimentSection from './SentimentSection';
import TableOfContents, { type TocItem } from './TableOfContents';

interface QueryCollapsibleProps {
  queryResult: QueryResultDetailed;
}

const QueryCollapsible = ({ queryResult }: QueryCollapsibleProps) => {
  const mentionedBrands = queryResult.mentionedBrands || [];
  const citations = queryResult.citations || [];
  const sentiments = queryResult.sentiments || [];

  const isFound = mentionedBrands.some((b) => b.isSelf);

  const ownSentiment = sentiments.find(
    (s) => s.brands.length > 0 && s.brands.every((b) => b.isSelf),
  );
  const competitorSentiment = sentiments.find(
    (s) => s.brands.length > 0 && s.brands.every((b) => !b.isSelf),
  );

  const hasSentiment = !!(ownSentiment || competitorSentiment);

  const tocItems: TocItem[] = useMemo(() => {
    const id = queryResult.id;
    const items: TocItem[] = [{ id: `summary-${id}`, label: 'AI Summary' }];
    if (mentionedBrands.length > 0) {
      items.push({ id: `brands-${id}`, label: `Brands (${mentionedBrands.length})` });
    }
    if (citations.length > 0) {
      items.push({ id: `citations-${id}`, label: `Citations (${citations.length})` });
    }
    if (hasSentiment) {
      items.push({ id: `sentiment-${id}`, label: 'Sentiment Analysis' });
    }
    return items;
  }, [queryResult.id, mentionedBrands.length, citations.length, hasSentiment]);

  const brandSentimentLabel = ownSentiment
    ? `${sentimentScoreToPercentage(ownSentiment.score)}% Brand sentiment`
    : 'No brand sentiment';

  const competitorSentimentLabel = competitorSentiment
    ? `${sentimentScoreToPercentage(competitorSentiment.score)}% Competitor sentiment`
    : 'No competitor sentiment';

  return (
    <Card className="yst-mb-6 !yst-overflow-visible">
      <Card.Content>
        <details className="yst-group">
          <summary className="yst-flex yst-items-start yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden">
            <div className="yst-flex yst-items-start yst-gap-3 yst-flex-1 yst-min-w-0">
              <div className="yst-w-20 yst-flex-shrink-0">
                <Badge
                  className={
                    isFound
                      ? 'yst-bg-green-100 yst-text-green-800'
                      : 'yst-bg-red-100 yst-text-red-800'
                  }
                >
                  {isFound ? 'Found' : 'Not found'}
                </Badge>
              </div>
              <div className="yst-flex-1 yst-min-w-0">
                <span className="yst-text-base yst-font-semibold">{queryResult.query}</span>
                <div className="yst-grid yst-grid-cols-2 md:yst-grid-cols-4 yst-text-sm yst-text-gray-500 yst-mt-2">
                  <span className="yst-flex yst-items-center yst-gap-1.5">
                    <BriefcaseIcon className="yst-h-4 yst-w-4" />
                    {mentionedBrands.length} brand
                    {mentionedBrands.length !== 1 ? 's' : ''}
                  </span>
                  <span className="yst-flex yst-items-center yst-gap-1.5">
                    <BookOpenIcon className="yst-h-4 yst-w-4" />
                    {citations.length} citation
                    {citations.length !== 1 ? 's' : ''}
                  </span>
                  <span className="yst-flex yst-items-center yst-gap-1.5">
                    <ThumbUpIcon className="yst-h-4 yst-w-4" />
                    {brandSentimentLabel}
                  </span>
                  <span className="yst-flex yst-items-center yst-gap-1.5">
                    <UsersIcon className="yst-h-4 yst-w-4" />
                    {competitorSentimentLabel}
                  </span>
                </div>
              </div>
            </div>
            <ChevronDownIcon className="yst-h-5 yst-w-5 yst-text-gray-400 yst-mt-1 yst-ml-4 yst-flex-shrink-0 group-open:yst-rotate-180 yst-transition-transform" />
          </summary>

          <div className="yst-mt-6 yst-pt-6 yst-border-t yst-flex yst-gap-6">
            {/* TOC sidebar */}
            <div className="yst-hidden md:yst-block md:yst-w-1/5 md:yst-flex-shrink-0">
              <TableOfContents items={tocItems} />
            </div>

            {/* Content area */}
            <div className="yst-flex-1 yst-min-w-0 [&>details:not([open]):not(:last-of-type)]:yst-border-b">
              <AISummarySection id={`summary-${queryResult.id}`} response={queryResult.response} />
              <BrandsSection id={`brands-${queryResult.id}`} mentionedBrands={mentionedBrands} />
              <CitationsSection id={`citations-${queryResult.id}`} citations={citations} />
              <SentimentSection
                id={`sentiment-${queryResult.id}`}
                ownSentiment={ownSentiment}
                competitorSentiment={competitorSentiment}
              />
            </div>
          </div>
        </details>
      </Card.Content>
    </Card>
  );
};

export default QueryCollapsible;
